/**
 * Central browser-tab-title mechanism (issue #443: every page was leaving the
 * tab on the static "Cockpit" from `index.html`).
 *
 * Format: "Cockpit - <Section>/<Tool>", where Section and Tool are the
 * catalog's own display names (`shared/catalog.ts` via `@/data/tools.config`),
 * never hand-typed per page, so a rename in the catalog flows through
 * automatically. A page that renders a sub-page keyed on its own heading (an
 * order's number, an account's name, ...) can append that as a trailing
 * "/<segment>" via `usePageTitleSegment`. Routes with no catalog entry
 * (login, settings, 404) fall back to the bare "Cockpit" default.
 *
 * `useDocumentTitle` is called once, from `Layout.tsx`, so most page files
 * need no changes at all. `usePageTitleSegment` bridges a page's own segment
 * up to that Layout-level call via a tiny external store (read with
 * `useSyncExternalStore`) instead of relying on effect-ordering between a
 * page and its ancestor Layout, which React does not guarantee.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { findTool } from '@/data/tools.config';
import { activeToolForPath } from '@/lib/toolRouting';
import { currentAppEnv, envTitlePrefix, type AppEnv } from '@/lib/appEnv';

const DEFAULT_TITLE = 'Cockpit';

// Mirrors the host check in `ui/index.html`, which prefixes the initial static
// title ("DEV · " or "STAGE · ") so dev, stage and prod tabs stay distinguishable.
// This hook re-derives the prefix on every title it sets (rather than reading back
// whatever `index.html` already wrote), since it replaces the whole title on every
// route change. The rule itself lives in `appEnv.ts`.

export function computeDocumentTitle(
  pathname: string,
  segment: string | undefined,
  /** The environment, or a plain boolean where true means dev. */
  env: AppEnv | boolean,
): string {
  const active = activeToolForPath(pathname);
  const found = active ? findTool(active.deptId, active.toolId) : undefined;
  const path = found
    ? [found.dept.name, found.tool.name, segment].filter(Boolean).join('/')
    : undefined;
  const title = path ? `${DEFAULT_TITLE} - ${path}` : DEFAULT_TITLE;
  const resolved: AppEnv = typeof env === 'boolean' ? (env ? 'dev' : 'prod') : env;
  return `${envTitlePrefix(resolved)}${title}`;
}

let pageTitleSegment: string | undefined;
const listeners = new Set<() => void>();

function getPageTitleSegment(): string | undefined {
  return pageTitleSegment;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Publishes `segment` as the tab title's trailing "/<segment>" for as long as
 * the calling component stays mounted with this value. Cleared on unmount (a
 * route change unmounts the outgoing page as part of it) or when a newer
 * value replaces it first; the identity check mirrors `useReportPageState` so
 * a stale cleanup can't clobber a page that has already taken the slot.
 */
export function usePageTitleSegment(segment: string | undefined): void {
  useEffect(() => {
    pageTitleSegment = segment;
    listeners.forEach((l) => l());
    return () => {
      if (pageTitleSegment === segment) {
        pageTitleSegment = undefined;
        listeners.forEach((l) => l());
      }
    };
  }, [segment]);
}

/**
 * Sets `document.title` from the current route's catalog entry. Called once,
 * from the router/layout level.
 */
export function useDocumentTitle(): void {
  const { pathname } = useLocation();
  const segment = useSyncExternalStore(subscribe, getPageTitleSegment);

  useEffect(() => {
    document.title = computeDocumentTitle(pathname, segment, currentAppEnv());
  }, [pathname, segment]);
}
